// IconGridSidebar.tsx - New sidebar component with icon grid layout
import React from 'react';
import Avatar from './Avatar';
// AppVersionBadge (2026-10-01): discreet version next to the wordmark (Jaime).
import { AppVersionBadge } from './AppVersionBadge';
// UX FIX (2026-10-16, Jaime report 23:41): restore the desktop brand header —
// single brand (the a753106 logo-dedup intent stands; NO duplicate gradient
// logo) reusing the exact Logo/AppVersionBadge pattern MobileMenu.tsx uses
// in its header so desktop matches mobile.
import { Logo } from './Logo';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LogOut, ChevronRight, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FIXED_SIDEBAR_ITEM_KEYS, HAMBURGER_NAVIGATION, isNavigationActive, navLabelKey } from '../config/navigation';
// I18N-2B (2026-09-23): sidebar labels resolve through the 'common' namespace.
import { useTranslation } from 'react-i18next';
// (2026-09-28, bug-batch#1 P2) English role strings ('Resident'/'Super Admin') now resolve
// through the centralized Spanish map.
import { getRoleLabel } from '../config/roleLabels';
import type { NavigationContext } from '../config/navigation';

interface SidebarItem {
  id: string;
  icon: React.ElementType;
  label: string;
  badge?: number;
}

interface SidebarGroup {
  title: string;
  items: SidebarItem[];
}

interface IconGridSidebarProps {
  groups: SidebarGroup[];
  activeItem: string;
  onItemClick: (id: string) => void;
  isCollapsed: boolean;
  onToggle: () => void;
  /** Mensajes unread badge (2026-09-17): total unread across conversations. */
  messagesUnread?: number;
  /** Active navigation context — drives the fixed registry entries' routes. */
  context?: NavigationContext;
}

export const IconGridSidebar: React.FC<IconGridSidebarProps> = ({
  groups, activeItem, onItemClick, isCollapsed, onToggle, context = 'condo',
  messagesUnread = 0
}) => {
  const { logout, user, ownedUnits, activeOwnedUnitId, roles } = useAuth();
  // I18N-2B: sidebar chrome + registry labels resolve via 'common'.
  const { t } = useTranslation('common');
  // FIX (Jaime bug 2026-09-09 PM, Francisco Caripa): the profile role label is
  // CONTEXT-aware — the Hogar lens shows the role in the ACTIVE HOME, not the
  // global user.role. A standalone own home always reads Propietario (even for
  // a user whose global role / other-condo membership is 'empleado'); an
  // employee-housing unit (assigned) reads Empleado; condo-linked home units
  // use their condo membership role. Condominio lens keeps the active
  // membership role with the global role as fallback.
  const activeSidebarUnit = (ownedUnits || []).find((unit) => unit.id === activeOwnedUnitId);
  const activeMembershipRole = activeSidebarUnit?.condo_id
    ? (roles || []).find((r) => r.condoId === activeSidebarUnit.condo_id)?.role
    : undefined;
  const sidebarRole = context === 'home'
    ? (activeSidebarUnit
        ? (activeSidebarUnit.assigned ? 'empleado' : (activeMembershipRole || 'propietario'))
        : (activeMembershipRole || user?.role || 'residente'))
    : (activeMembershipRole || user?.role);
  const navigate = useNavigate();
  // FIX 2026-09-16 (Jaime ruling, role display semantics): the profile block
  // rendered the raw role key; supplier accounts showed "provider" and
  // claim-created/no-unit accounts (role 'visitante') showed the raw value.
  // Map the display labels here; other roles keep the legacy behavior.
  const SIDEBAR_ROLE_LABELS: Record<string, string> = {
    visitante: t('role.visitante'),
    provider: t('role.provider'),
    proveedor: t('role.provider'),
  };
  // (Jaime 2026-09-03) Fixed pinned entries are now REGISTRY-DRIVEN (keys:
  // mensajes + complaints) instead of hard-coded buttons. Placement is preserved:
  // fixed directly above the user-info block, in FIXED_SIDEBAR_ITEM_KEYS order.
  // Both items are alwaysVisible/ungated in the registry (Jaime hard rule).
  const fixedItems = FIXED_SIDEBAR_ITEM_KEYS
    .map((key) => HAMBURGER_NAVIGATION.find((item) => item.key === key))
    .filter((item): item is NonNullable<typeof item> => !!item);

  const sidebarVariants = {
    expanded: { width: 280 },
    collapsed: { width: 80 }
  };

  return (
    <motion.aside
      initial="expanded"
      animate={isCollapsed ? "collapsed" : "expanded"}
      variants={sidebarVariants}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="sticky left-0 top-0 h-full bg-[var(--v-surface-1)] border-r border-[var(--v-border-light)] z-30 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[var(--v-border-light)]">
        {/* UX FIX (2026-10-16, Jaime report 23:41): commit a753106 (Oct 13 logo
            dedup) removed the whole desktop header block (wordmark +
            AppVersionBadge), so the desktop sidebar header showed only the
            toggle button while mobile still showed the version. Restored with
            the exact MobileMenu header pattern: one logo image + 'Vecindus'
            wordmark + <AppVersionBadge />; hidden while collapsed, same as the
            removed block. */}
        {!isCollapsed && (
          <div className="flex min-w-0 items-center gap-2">
            <Logo withText={true} size={28} />
            <AppVersionBadge />
          </div>
        )}
        <button
          onClick={onToggle}
          className="p-2 rounded-xl hover:bg-[var(--v-surface-2)] transition-colors"
        >
          {isCollapsed ? <ChevronRight className="w-5 h-5 text-[var(--v-text-secondary)]" /> : <X className="w-5 h-5 text-[var(--v-text-secondary)]" />}
        </button>
      </div>

      {/* Navigation Groups */}
      <div className="flex-1 overflow-y-auto py-4 px-2">
        {groups.map((group) => (
          <div key={group.title} className="mb-6">
            {!isCollapsed && (
              <h3 className="px-3 text-xs font-bold text-[var(--v-text-secondary)] uppercase tracking-wider mb-3 opacity-60">
                {group.title}
              </h3>
            )}
            <div className="grid grid-cols-1 gap-1">
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = isNavigationActive(activeItem, { routes: { home: item.id, condo: item.id }, label: item.label, key: item.id, icon: item.icon as LucideIcon });
                
                return (
                  <button
                    key={item.id}
                    onClick={() => onItemClick(item.id)}
                    className={`
                      relative flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200
                      ${isActive 
                        ? 'bg-gradient-to-r from-[#36CCD8]/10 to-[#2DB8C4]/10 border border-[#36CCD8]/30' 
                        : 'hover:bg-[var(--v-surface-2)] border border-transparent'
                      }
                      ${isCollapsed ? 'justify-center' : ''}
                    `}
                  >
                    <div className={`
                      p-2 rounded-lg transition-colors
                      ${isActive ? 'bg-[#36CCD8] text-white' : 'bg-[var(--v-surface-2)] text-[var(--v-text-secondary)]'}
                    `}>
                      <Icon className="w-5 h-5" />
                    </div>
                    
                    {!isCollapsed && (
                      <div className="flex-1 text-left">
                        <span className={`text-sm font-medium ${isActive ? 'text-[#36CCD8]' : 'text-[var(--v-text-primary)]'}`}>
                          {item.label}
                        </span>
                      </div>
                    )}
                    
                    {/* Badge (2026-09-17): expanded shows the count pill; collapsed
                        shows a small red dot anchored to the icon so hidden counts
                        are still discoverable. */}
                    {item.badge && !isCollapsed && (
                      <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        {item.badge}
                      </span>
                    )}
                    {item.badge && isCollapsed && (
                      <span className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-red-500" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Fixed pinned entries — registry-driven (2026-09-03, Jaime hard rule:
          Mensajes + Quejas y Sugerencias ALWAYS visible for ALL users, ungated),
          placed directly above the user-info block (spec 2026-08-29). Deliberately
          NOT part of the scrollable group lists so they stay pinned here. */}
      <div className="flex-shrink-0 border-t border-[var(--v-border-light)] px-2 pt-3 pb-1">
        {fixedItems.map((item, index) => {
          const Icon = item.icon;
          const isActive = isNavigationActive(activeItem, item);
          return (
            <button
              key={item.key}
              onClick={() => navigate(item.routes[context])}
              className={`
                relative w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200
                ${index > 0 ? 'mt-1' : ''}
                ${isActive
                  ? 'bg-gradient-to-r from-[#36CCD8]/10 to-[#2DB8C4]/10 border border-[#36CCD8]/30'
                  : 'hover:bg-[var(--v-surface-2)] border border-transparent'}
                ${isCollapsed ? 'justify-center' : ''}
              `}
            >
              <div className={`
                p-2 rounded-lg transition-colors
                ${isActive ? 'bg-[#36CCD8] text-white' : 'bg-[var(--v-surface-2)] text-[var(--v-text-secondary)]'}
              `}>
                <Icon className="w-5 h-5" />
              </div>
              {!isCollapsed && (
                <div className="flex-1 text-left">
                  <span className={`text-sm font-medium ${isActive ? 'text-[#36CCD8]' : 'text-[var(--v-text-primary)]'}`}>
                    {t(navLabelKey(item.key), { defaultValue: item.label })}
                  </span>
                  {item.badge && <span className="ml-2 rounded-full bg-[var(--v-teal-100)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--v-teal-700)]" data-testid={`nav-badge-${item.key}`}>{item.badge}</span>}
                </div>
              )}
              {/* Mensajes unread badge (2026-09-17, Jaime-approved Option 1):
                  red count pill when expanded, small red dot when collapsed. */}
              {item.key === 'mensajes' && messagesUnread > 0 && (
                !isCollapsed ? (
                  <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-testid="mensajes-unread-badge">
                    {messagesUnread > 99 ? '99+' : messagesUnread}
                  </span>
                ) : (
                  <span className="absolute top-2 right-2 w-2.5 h-2.5 rounded-full bg-red-500" data-testid="mensajes-unread-dot" />
                )
              )}
            </button>
          );
        })}
      </div>

      {/* User Profile Section */}
      <div className="border-t border-[var(--v-border-light)] p-4">
        {/* UX FIX (2026-10-16): collapsed keeps at least the avatar visible so
            the user stays identifiable (the p-3 wrapper previously left too
            little room for the 40px avatar at the 80px collapsed width);
            expanded keeps name+role exactly as before. */}
        <div className={`flex items-center gap-3 rounded-xl bg-[var(--v-surface-2)] ${isCollapsed ? 'justify-center p-1' : 'p-3'}`}>
          {/* FEATURE (2026-09-01, Jaime ruling): shared <Avatar> — photo when user.avatarUrl exists. */}
          <Avatar name={user?.name} avatarUrl={user?.avatarUrl} size={40} />
          {!isCollapsed && (
            <div className="flex-1">
              <p className="text-sm font-bold text-[var(--v-text-primary)] truncate">{user?.name || t('common.user')}</p>
              <p className="text-xs text-[var(--v-text-secondary)] capitalize">{SIDEBAR_ROLE_LABELS[sidebarRole || user?.role || ''] || getRoleLabel(sidebarRole || user?.role)}</p>
            </div>
          )}
        </div>
        
        <button
          onClick={logout}
          className={`mt-2 w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[var(--v-danger)] hover:bg-[var(--v-danger-bg)] transition-colors ${isCollapsed ? 'justify-center' : ''}`}
        >
          <LogOut className="w-5 h-5" />
          {!isCollapsed && <span className="text-sm font-medium">{t('common.logout')}</span>}
        </button>
      </div>
    </motion.aside>
  );
};

export default IconGridSidebar;
